/**
 * The buying committee — **the link, not the list**.
 *
 * `opportunity_contacts` is what the stage gates read: `@contact` is satisfied
 * by the existence of a link row, and `@design_engineer_contact` by a link row
 * whose EFFECTIVE `buying_role` is `design_engineer`. A contact sitting on the
 * account satisfies neither, by design (§5.3) — linking is a deliberate act.
 * So until this section could write, a hand-created opportunity was stuck at
 * Prospect permanently and the gate named a field nobody could supply.
 *
 * Three decisions are visible on screen rather than only in a commit message:
 *
 *  - **This picks an existing person; it does not create one.** Creating a
 *    contact and linking one are different operations against different
 *    parents with different write rules (§2), and the contact record is the
 *    one place a person's canonical role is chosen. `ContactsPanel` on the
 *    account or the rep firm already does that, so this links to it rather
 *    than growing a second, subtly different copy of the same form.
 *  - **The per-deal override names its own fallback.** Each control's first
 *    option reads "Same as contact record (…)" with the contact's own value
 *    spelled out, so an unset override is not a blank — it says what it falls
 *    back to, and clearing the override is the same control rather than a
 *    separate gesture. The resolution itself is never recomputed here: the
 *    effective values come from `OPPORTUNITY_CONTACT_DEF`'s `coalesce`, which
 *    is the same expression the sentinel resolves with.
 *  - **A channel deal reaches the rep firm's people.** A contact hangs off
 *    exactly ONE parent (§5.2), so a distributor salesperson is never on the
 *    account. The picker offers the account's contacts and, when the
 *    opportunity carries a `rep_firm_id`, that firm's channel contacts beside
 *    them — the opportunity's firm, not the account's, because the deal is
 *    what decides which firm is in the loop.
 */

import { useState } from 'react';
import { Plus, Unlink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BUYING_ROLES, DISPOSITIONS, INFLUENCE_LEVELS } from '@menlo/shared';
import type { EntityContext } from '@menlo/shared';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/label';
import { useApiMutation, useApiQuery, type Paged, type Row } from '@/lib/queries';
import { humanize } from '@/lib/format';

type Person = EntityContext['people'][number];

/** The three per-deal override columns, in the order they read on the card. */
const OVERRIDES = [
  {
    column: 'buying_role',
    effective: 'effective_buying_role',
    label: 'Role',
    values: BUYING_ROLES,
  },
  {
    column: 'influence_level',
    effective: 'effective_influence_level',
    label: 'Influence',
    values: INFLUENCE_LEVELS,
  },
  {
    column: 'disposition',
    effective: 'effective_disposition',
    label: 'Disposition',
    values: DISPOSITIONS,
  },
] as const;

function dispositionVariant(value: string): 'success' | 'danger' | 'outline' {
  if (value === 'champion') return 'success';
  if (value === 'detractor') return 'danger';
  return 'outline';
}

export function BuyingCommittee({
  opportunityId,
  accountId,
  accountName,
  repFirmId,
  repFirmName,
  people,
  canWrite,
}: {
  opportunityId: string;
  accountId: string | null;
  accountName: string | null;
  repFirmId: string | null;
  repFirmName: string | null;
  /** The server's own resolution, already loaded — shown until the links arrive. */
  people: EntityContext['people'];
  canWrite: boolean;
}): React.ReactElement {
  const [adding, setAdding] = useState(false);

  const linksKey = ['opportunity-contacts', opportunityId];
  const links = useApiQuery<Paged<Row>>(
    linksKey,
    `/api/opportunity-contacts?opportunityId=${opportunityId}&pageSize=200`,
  );

  // The contact records themselves, for the value an unset override falls back
  // to and for the picker. Same keys and same page size `ContactsPanel` uses,
  // so the two share a cache entry rather than racing each other.
  const accountKey = ['contacts', `accountId=${accountId ?? ''}`];
  const accountContacts = useApiQuery<Paged<Row>>(
    accountKey,
    accountId === null ? null : `/api/contacts?accountId=${accountId}&pageSize=200`,
  );
  const repFirmKey = ['contacts', `repFirmId=${repFirmId ?? ''}`];
  const repFirmContacts = useApiQuery<Paged<Row>>(
    repFirmKey,
    repFirmId === null ? null : `/api/contacts?repFirmId=${repFirmId}&pageSize=200`,
  );

  const contactsById = new Map<string, Row>();
  for (const contact of [
    ...(accountContacts.data?.items ?? []),
    ...(repFirmContacts.data?.items ?? []),
  ]) {
    contactsById.set(String(contact['id']), contact);
  }

  const peopleById = new Map(people.map((person) => [person.contact_id, person]));
  const linkRows = links.data?.items ?? null;

  const invalidate = [linksKey, ['opportunity', opportunityId]];

  const update = useApiMutation<Row, { id: string; body: Record<string, unknown> }>(
    ({ id, body }) => ({ path: `/api/opportunity-contacts/${id}`, method: 'PUT', body }),
    { invalidate },
  );

  const unlink = useApiMutation<Row, string>(
    (id) => ({ path: `/api/opportunity-contacts/${id}`, method: 'DELETE' }),
    { invalidate },
  );

  // Until the link rows land there is no `id` to write against, so the section
  // renders what the entity context already gave it and no controls — an
  // unlink button that cannot say which row it means is worse than none.
  const members: {
    key: string;
    linkId: string | null;
    contactId: string;
    name: string;
    person: Person | undefined;
    contact: Row | undefined;
    row: Row | undefined;
  }[] =
    linkRows === null
      ? people.map((person) => ({
          key: person.contact_id,
          linkId: null,
          contactId: person.contact_id,
          name: `${person.first_name} ${person.last_name}`,
          person,
          contact: contactsById.get(person.contact_id),
          row: undefined,
        }))
      : [...linkRows]
          .sort((a, b) =>
            `${String(a['last_name'])} ${String(a['first_name'])}`.localeCompare(
              `${String(b['last_name'])} ${String(b['first_name'])}`,
            ),
          )
          .map((row) => {
            const contactId = String(row['contact_id']);
            return {
              key: String(row['id']),
              linkId: String(row['id']),
              contactId,
              name: `${String(row['first_name'])} ${String(row['last_name'])}`,
              person: peopleById.get(contactId),
              contact: contactsById.get(contactId),
              row,
            };
          });

  const linkedIds = new Set(members.map((member) => member.contactId));

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Buying committee</h3>
        {canWrite && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Link contact
          </Button>
        )}
      </div>

      <ul className="grid gap-2 sm:grid-cols-2">
        {members.map((member) => {
          const { row, person, contact } = member;
          const isChannel =
            contact === undefined ? (person?.is_channel ?? false) : contact['rep_firm_id'] !== null;
          const title =
            person?.title ?? (contact === undefined ? null : (contact['title'] as string | null));

          return (
            <li key={member.key} className="rounded-md border p-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {member.name}
                    {isChannel && (
                      <Badge variant="info" className="ml-1.5">
                        Channel
                      </Badge>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{title ?? '—'}</p>
                </div>
                {canWrite && member.linkId !== null && (
                  <button
                    type="button"
                    aria-label={`Unlink ${member.name}`}
                    title="Remove from this opportunity. The contact record is untouched."
                    onClick={() => unlink.mutate(member.linkId as string)}
                    className="rounded p-1 text-muted-foreground hover:text-destructive"
                  >
                    <Unlink className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
              </div>

              {canWrite && row !== undefined ? (
                <div className="mt-2 space-y-1">
                  {OVERRIDES.map((field) => {
                    const override = row[field.column];
                    const own = contact?.[field.column];
                    const effective = String(row[field.effective] ?? '');
                    return (
                      <Select
                        key={field.column}
                        aria-label={`${field.label} on this opportunity for ${member.name}`}
                        className="h-7 text-xs"
                        value={typeof override === 'string' ? override : ''}
                        onChange={(event) =>
                          update.mutate({
                            id: member.linkId as string,
                            body: {
                              [field.column]: event.target.value === '' ? null : event.target.value,
                            },
                          })
                        }
                      >
                        <option value="">
                          {field.label}: same as contact record
                          {typeof own === 'string'
                            ? ` (${humanize(own)})`
                            : effective === '' || override !== null
                              ? ''
                              : ` (${humanize(effective)})`}
                        </option>
                        {field.values.map((value) => (
                          <option key={value} value={value}>
                            {field.label} on this deal: {humanize(value)}
                          </option>
                        ))}
                      </Select>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-1 flex flex-wrap gap-1">
                  {OVERRIDES.map((field) => {
                    const value =
                      row === undefined
                        ? String(
                            person?.[
                              field.column as 'buying_role' | 'influence_level' | 'disposition'
                            ] ?? '',
                          )
                        : String(row[field.effective] ?? '');
                    if (value === '') return null;
                    return (
                      <Badge
                        key={field.column}
                        variant={
                          field.column === 'disposition' ? dispositionVariant(value) : 'outline'
                        }
                      >
                        {humanize(value)}
                      </Badge>
                    );
                  })}
                </p>
              )}
            </li>
          );
        })}

        {members.length === 0 && (
          <li className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            Nobody linked yet. Qualified needs a reachable technical contact; Design Win needs a
            design engineer. A contact on the account does not count until it is linked here.
          </li>
        )}
      </ul>

      <p className="text-[11px] text-muted-foreground">
        Roles shown are the effective ones: an override set on this opportunity wins over the
        contact record.
      </p>

      {adding && (
        <LinkContactDialog
          opportunityId={opportunityId}
          accountName={accountName}
          accountId={accountId}
          repFirmName={repFirmName}
          repFirmId={repFirmId}
          accountContacts={accountContacts.data?.items ?? []}
          repFirmContacts={repFirmContacts.data?.items ?? []}
          linkedIds={linkedIds}
          invalidate={invalidate}
          onClose={() => setAdding(false)}
        />
      )}
    </section>
  );
}

/**
 * Pick a person who already exists, optionally with a role for this deal only.
 *
 * The two groups are the two parents a contact can have. When the opportunity
 * has no rep firm there is only one group and the `optgroup` is still labelled,
 * because "which of these people is the distributor" is the question a reader
 * of a channel deal asks first.
 */
function LinkContactDialog({
  opportunityId,
  accountId,
  accountName,
  repFirmId,
  repFirmName,
  accountContacts,
  repFirmContacts,
  linkedIds,
  invalidate,
  onClose,
}: {
  opportunityId: string;
  accountId: string | null;
  accountName: string | null;
  repFirmId: string | null;
  repFirmName: string | null;
  accountContacts: Row[];
  repFirmContacts: Row[];
  linkedIds: ReadonlySet<string>;
  invalidate: readonly (readonly unknown[])[];
  onClose: () => void;
}): React.ReactElement {
  const [contactId, setContactId] = useState('');
  const [role, setRole] = useState('');

  // Already linked, or retired. An inactive person stays visible on a link
  // that exists — the history is real — but is not offered as a new one.
  const selectable = (items: Row[]): Row[] =>
    items.filter(
      (contact) => !linkedIds.has(String(contact['id'])) && contact['is_active'] !== false,
    );

  const fromAccount = selectable(accountContacts);
  const fromRepFirm = selectable(repFirmContacts);
  const nobody = fromAccount.length === 0 && fromRepFirm.length === 0;

  const create = useApiMutation<Row, void>(
    () => ({
      path: '/api/opportunity-contacts',
      method: 'POST',
      body: {
        opportunity_id: opportunityId,
        contact_id: contactId,
        // Sent only when chosen: an override of `null` and an absent override
        // are the same thing, and the gate reads the contact's own value in
        // both cases.
        ...(role === '' ? {} : { buying_role: role }),
      },
    }),
    { invalidate, onSuccess: onClose },
  );

  const option = (contact: Row): React.ReactElement => (
    <option key={String(contact['id'])} value={String(contact['id'])}>
      {String(contact['first_name'])} {String(contact['last_name'])}
      {contact['title'] === null || contact['title'] === undefined
        ? ''
        : ` — ${String(contact['title'])}`}{' '}
      ({humanize(String(contact['buying_role']))})
    </option>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title="Link a contact to this opportunity"
      description="Linking is what the stage gates read. A contact on the account is not on the deal until it is linked here."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={contactId === '' || create.isPending}>
            Link contact
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Contact">
          <Select value={contactId} onChange={(event) => setContactId(event.target.value)}>
            <option value="">Choose someone…</option>
            <optgroup label={accountName ?? 'Account contacts'}>{fromAccount.map(option)}</optgroup>
            {repFirmId !== null && (
              <optgroup label={`${repFirmName ?? 'Rep firm'} — channel`}>
                {fromRepFirm.map(option)}
              </optgroup>
            )}
          </Select>
        </Field>

        <Field
          label="Role on this opportunity"
          hint="Optional, and this deal only. Left unset, the contact's own buying role applies — which is what Design Win checks."
        >
          <Select value={role} onChange={(event) => setRole(event.target.value)}>
            <option value="">Same as contact record</option>
            {BUYING_ROLES.map((value) => (
              <option key={value} value={value}>
                {humanize(value)}
              </option>
            ))}
          </Select>
        </Field>

        {nobody && (
          <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            Nobody left to link. Contacts are created on the record they belong to — a customer
            contact on the account, a distributor salesperson on the rep firm.{' '}
            {accountId !== null && (
              <Link to={`/accounts/${accountId}`} className="underline">
                Add one on {accountName ?? 'the account'}
              </Link>
            )}
            {repFirmId !== null && (
              <>
                {' or '}
                <Link to={`/rep-firms/${repFirmId}`} className="underline">
                  on {repFirmName ?? 'the rep firm'}
                </Link>
              </>
            )}
            .
          </p>
        )}
      </div>
    </Dialog>
  );
}
